import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, Download, Trash2, Plus, FileCheck2, Clock, Eye } from "lucide-react";
import { createDeposit, listDeposits, deleteDeposit, downloadDeposit, viewDeposit } from "@/lib/depotApi";

const T = {
  fr: {
    title: "Dépôt de carnet (patient → praticien)",
    intro: "Générez un lien de dépôt sécurisé et transmettez-le à votre patient. Il pourra y déposer son carnet PDF ; vous le retrouverez ici pour le télécharger en séance.",
    create: "Créer un lien de dépôt",
    retention: "Les dépôts sont automatiquement supprimés après 30 jours.",
    empty: "Aucun lien de dépôt pour le moment.",
    pending: "En attente du dépôt",
    received: "Carnet reçu",
    linkLabel: "Lien à transmettre au patient",
    copy: "Copier le lien",
    copied: "Lien copié",
    download: "Télécharger le PDF",
    view: "Aperçu",
    del: "Supprimer",
    expires: "Expire le",
    newBadge: (n) => `${n} nouveau${n > 1 ? "x" : ""}`,
    rowNew: "Nouveau",
    createErr: "Impossible de créer le lien.",
    delOk: "Dépôt supprimé",
    confirmDel: "Supprimer ce dépôt ?",
  },
  es: {
    title: "Depósito de cuaderno (paciente → profesional)",
    intro: "Genere un enlace de depósito seguro y envíelo a su paciente. Podrá depositar su cuaderno en PDF; lo encontrará aquí para descargarlo en sesión.",
    create: "Crear un enlace de depósito",
    retention: "Los depósitos se eliminan automáticamente después de 30 días.",
    empty: "Ningún enlace de depósito por ahora.",
    pending: "Esperando el depósito",
    received: "Cuaderno recibido",
    linkLabel: "Enlace para enviar al paciente",
    copy: "Copiar el enlace",
    copied: "Enlace copiado",
    download: "Descargar el PDF",
    view: "Vista previa",
    del: "Eliminar",
    expires: "Caduca el",
    newBadge: (n) => `${n} nuevo${n > 1 ? "s" : ""}`,
    rowNew: "Nuevo",
    createErr: "No se pudo crear el enlace.",
    delOk: "Depósito eliminado",
    confirmDel: "¿Eliminar este depósito?",
  },
};

export default function DepotsPanel({ lang = "fr", onBadge }) {
  const t = T[lang];
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const locale = lang === "es" ? "es-ES" : "fr-FR";
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(locale); } catch { return iso; } };
  const depotUrl = (token) => `${window.location.origin}/p/depot/${token}`;
  const sujetsUrl = (token) => `${window.location.origin}/p/sujets-therapeutiques?depot=${token}`;
  const onCopySujets = (token) => navigator.clipboard.writeText(sujetsUrl(token)).then(() => toast.success(lang === "es" ? "Enlace « Mis temas » copiado" : "Lien « Mes sujets thérapeutiques » copié : la synthèse arrivera directement ici")).catch(() => {});
  const schemasUrl = (token) => `${window.location.origin}/p/schemas-pensees?depot=${token}`;
  const onCopySchemas = (token) => navigator.clipboard.writeText(schemasUrl(token)).then(() => toast.success(lang === "es" ? "Enlace « Pensamientos » copiado" : "Lien « Pensées automatiques » copié : le cahier arrivera directement ici")).catch(() => {});
  const driversUrl = (token) => `${window.location.origin}/p/drivers-exo?depot=${token}`;
  const onCopyDrivers = (token) => navigator.clipboard.writeText(driversUrl(token)).then(() => toast.success(lang === "es" ? "Enlace « Drivers » copiado" : "Lien « Drivers » copié : la synthèse arrivera directement ici")).catch(() => {});
  const assqUrl = (token) => `${window.location.origin}/p/assq?depot=${token}`;
  const onCopyAssq = (token) => navigator.clipboard.writeText(assqUrl(token)).then(() => toast.success(lang === "es" ? "Enlace « ASSQ » copiado" : "Lien « ASSQ » copié : la synthèse arrivera directement ici")).catch(() => {});
  const autogratUrl = (token) => `${window.location.origin}/p/autogratification?depot=${token}`;
  const onCopyAutograt = (token) => navigator.clipboard.writeText(autogratUrl(token)).then(() => toast.success(lang === "es" ? "Enlace « Mi autogratificación » copiado" : "Lien « Mon autogratification » copié : la synthèse hebdomadaire arrivera directement ici")).catch(() => {});

  const refresh = () => listDeposits().then((d) => setDeposits(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const onCreate = async () => {
    setCreating(true);
    try { await createDeposit(); await refresh(); }
    catch { toast.error(t.createErr); }
    setCreating(false);
  };
  const onCopy = (token) => navigator.clipboard.writeText(depotUrl(token)).then(() => toast.success(t.copied)).catch(() => {});
  const onDownload = (d) => downloadDeposit(d.id, d.filename).then(() => refresh()).catch(() => toast.error("Erreur"));
  const onView = (d) => viewDeposit(d.id).then(() => refresh()).catch(() => toast.error("Erreur"));
  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDel)) return;
    try { await deleteDeposit(id); toast.success(t.delOk); refresh(); } catch { toast.error("Erreur"); }
  };

  const newCount = deposits.filter((d) => d.status === "received" && !d.downloaded_at).length;
  useEffect(() => { onBadge?.(newCount); }, [newCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="prat-depots-body">
      <button onClick={onCreate} disabled={creating} data-testid="depot-create-btn"
        className="inline-flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-full bg-[var(--gold)] text-white hover:brightness-105 transition-all disabled:opacity-60 mb-2">
        <Plus size={15} /> {t.create}
      </button>
      <p className="text-xs text-[var(--ink-soft)] mb-6">{t.retention}</p>

      {loading ? null : deposits.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)]" data-testid="depot-empty">{t.empty}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4" data-testid="depot-list">
          {deposits.map((d) => (
            <div key={d.id} data-testid={`depot-row-${d.id}`} className="rounded-2xl bg-white/60 border border-[var(--border)] p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${d.status === "received" ? "text-[#6f7d54]" : "text-[var(--ink-soft)]"}`} data-testid={`depot-status-${d.id}`}>
                  {d.status === "received" ? <FileCheck2 size={16} /> : <Clock size={16} />}
                  {d.status === "received" ? t.received : t.pending}
                  {d.status === "received" && !d.downloaded_at && (
                    <span data-testid={`depot-new-${d.id}`} className="ml-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#B0553B] text-white">{t.rowNew}</span>
                  )}
                </span>
                <span className="text-xs text-[var(--ink-soft)]">{t.expires} {fmt(d.expires_at)}</span>
              </div>
              <p className="text-xs text-[var(--ink-soft)] mb-1">{t.linkLabel}</p>
              <div className="rounded-xl bg-[var(--sand)] border border-[var(--border)] px-3 py-2 overflow-x-auto mb-3">
                <code data-testid={`depot-url-${d.id}`} className="text-xs text-[var(--ink-soft)] break-all">{depotUrl(d.token)}</code>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => onCopy(d.token)} data-testid={`depot-copy-${d.id}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full bg-[var(--gold)] text-white hover:brightness-105 transition-all">
                  <LinkIcon size={14} /> {t.copy}
                </button>
                {d.status !== "received" && (
                  <button onClick={() => onCopySujets(d.token)} data-testid={`depot-copy-sujets-${d.id}`} title={sujetsUrl(d.token)}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <LinkIcon size={14} /> {lang === "es" ? "Enlace « Mis temas » + depósito" : "Lien « Mes sujets » + dépôt"}
                  </button>
                )}
                {d.status !== "received" && (
                  <button onClick={() => onCopyAutograt(d.token)} data-testid={`depot-copy-autograt-${d.id}`} title={autogratUrl(d.token)}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <LinkIcon size={14} /> {lang === "es" ? "Enlace « Autogratificación » + depósito" : "Lien « Autogratification » + dépôt"}
                  </button>
                )}
                {d.status !== "received" && (
                  <button onClick={() => onCopyAssq(d.token)} data-testid={`depot-copy-assq-${d.id}`} title={assqUrl(d.token)}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <LinkIcon size={14} /> {lang === "es" ? "Enlace « ASSQ » + depósito" : "Lien « ASSQ » + dépôt"}
                  </button>
                )}
                {d.status !== "received" && (
                  <button onClick={() => onCopyDrivers(d.token)} data-testid={`depot-copy-drivers-${d.id}`} title={driversUrl(d.token)}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <LinkIcon size={14} /> {lang === "es" ? "Enlace « Drivers » + depósito" : "Lien « Drivers » + dépôt"}
                  </button>
                )}
                {d.status !== "received" && (
                  <button onClick={() => onCopySchemas(d.token)} data-testid={`depot-copy-schemas-${d.id}`} title={schemasUrl(d.token)}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <LinkIcon size={14} /> {lang === "es" ? "Enlace « Pensamientos » + depósito" : "Lien « Pensées » + dépôt"}
                  </button>
                )}
                {d.status === "received" && (
                  <button onClick={() => onView(d)} data-testid={`depot-view-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <Eye size={14} /> {t.view}
                  </button>
                )}
                {d.status === "received" && (
                  <button onClick={() => onDownload(d)} data-testid={`depot-download-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
                    <Download size={14} /> {t.download}
                  </button>
                )}
                <button onClick={() => onDelete(d.id)} data-testid={`depot-delete-${d.id}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--border)] text-[var(--ink-soft)] hover:border-[#B0553B] hover:text-[#B0553B] transition-colors">
                  <Trash2 size={14} /> {t.del}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
